<template>
  <div class="edit-field" :class="mainClasses" @click.stop>
    <span
      v-if="!editMode"
      class="edit-field__value"
      :class="valueClasses"
      @click="onClickEdit"
    >
      {{ value || EMPTY_VALUE_PLACEHOLDER }}
    </span>
    <div v-else class="edit-field__input" :class="inputClasses">
      <component
        :is="component"
        :instance
        size="xs"
        @mouseup.stop
        @mousedown.stop
        @click:enter="handleReset"
        @click:tab="handleReset"
        @click:escape="handleReset"
        @ready="onReady"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { TransactionClass } from '@types'
import { EMPTY_VALUE_PLACEHOLDER } from '@/const'

type Props = {
  instance: TransactionClass
  method: string
  display?: string
  value?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
}

type Emits = {
  edit: [string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  reset: (field?: string) => {
    if (field === props.method) return
    editMode.value = false
  },
})

const editMode = ref(false)

const mainClasses = computed(() => ({
  'edit-field--active': editMode.value,
}))

const inputClasses = computed(() => ({
  [`edit-field__input--${props.size}`]: props.size,
}))

const component = computed(() =>
  // @ts-expect-error - props.instance is a TransactionClass
  props.instance?.[props.method](),
)

const value = computed(() => {
  // @ts-expect-error - props.instance is a TransactionClass
  return props.display ? props.instance?.[props.display] : props.value
})

const valueClasses = computed(() =>
  props.display?.startsWith('displayAmount')
    ? {
      'edit-field__value--amount number-node': true,
      'edit-field__value--amount-negative': value.value?.startsWith('-'),
    }
    : undefined,
)

const onClickEdit = () => {
  editMode.value = true
  emit('edit', props.method)
}

const handleReset = (event: Event) => {
  event?.stopPropagation()
  event?.preventDefault()
  editMode.value = false
}

const onReady = (e: any) => {
  e.$el ? e.$el.focus() : e.focus()
}
</script>

<style>
.edit-field {
  @apply inline-block;

  &--active {
    @apply flex;
  }

  &__value {
    @apply flex-1;
    @apply outline outline-1 outline-transparent;
    @apply rounded-sm;
    @apply hover:outline-common-border dark:hover:outline-light-border;
    @apply whitespace-nowrap;

    &--amount {
      @apply text-slate-950 dark:text-green-400;
    }

    &--amount-negative {
      @apply text-red-600 dark:text-red-400;
    }
  }

  &__input {
    @apply w-full;
    @apply flex-auto;
    @apply -my-[.45rem];

    &--xs {
      @apply max-w-[7rem];
    }

    &--sm {
      @apply max-w-[9rem];
    }

    &--md {
      @apply max-w-[12rem];
    }
  }
}
</style>
